import { ApiService } from 'app/api/index';
import { IApplicationContext } from 'app/context/types';
import { OrderEnum } from 'modules/shared/enums';
import { deleteEmptyObjectProperties } from 'modules/utilities/Helpers';
import { SearchEndUsersFieldsForm } from '../components/EndUsersTableSearchBar';

type EndUsersSearchQuery = SearchEndUsersFieldsForm & {
	page?: number;
	orderByOrder: OrderEnum;
	orderColumn: string;
};

export class EndUsersManagementService {
	private apiService: ApiService;

	constructor(private applicationContext: IApplicationContext) {
		this.apiService = new ApiService(this.applicationContext);
	}

	getEndUsers(searchQuery: EndUsersSearchQuery) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.GET_END_USERS,
					null,
					{
						pathParams: this.formatEndUsersSearchQuery(searchQuery),
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	exportEndUsersToCsv(searchQuery: EndUsersSearchQuery) {
		return new Promise<string>((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.EXPORT_END_USERS_TO_CSV,
					null,
					{
						pathParams: this.formatEndUsersSearchQuery(searchQuery),
					},
				)
				.then(data => {
					resolve(data as string);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	searchEndUsersByName(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.SEARCH_END_USERS_BY_NAME,
					null,
					{
						pathParams: { searchKey },
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	searchEndUsersByEmail(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.SEARCH_END_USERS_BY_EMAIL,
					null,
					{
						pathParams: { searchKey },
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	lockEndUser(endUserId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.LOCK_END_USER_RESET_PASS_ACTION,
					null,
					{
						reqPathParam: endUserId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	unlockEndUser(endUserId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.UNLOCK_END_USER_RESET_PASS_ACTION,
					null,
					{
						reqPathParam: endUserId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	sendEndUserResetPassword(endUserId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.SEND_END_USER_RESET_PASS_ACTION,
					null,
					{
						reqPathParam: endUserId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUser(endUserId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
						.GET_END_USER,
					null,
					{
						reqPathParam: endUserId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUserCompletedLessons(
		endUserId: number,
		searchQuery: {
			page: number;
			region: number;
			lessonType: number;
			lessonSearchKey: string;
		},
	) {
		const req =
			this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
				.GET_END_USER_COMPLETED_LESSONS;
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					{ ...req, url: req.url.replace(':endUserId', endUserId) },
					null,
					{
						pathParams: deleteEmptyObjectProperties(searchQuery, [-1, '-1']),
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUserVocabLessonWordsReview(endUserId: number, lessonId: number) {
		const req =
			this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
				.GET_END_USER_VOCAB_LESSON_WORDS_REVIEW;
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					{
						...req,
						url: req.url
							.replace(':endUserId', endUserId)
							.replace(':lessonId', lessonId),
					},
					null,
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUserVerbLessonWordsReview(endUserId: number, lessonId: number) {
		const req =
			this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
				.GET_END_USER_VERB_LESSON_WORDS_REVIEW;
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					{
						...req,
						url: req.url
							.replace(':endUserId', endUserId)
							.replace(':lessonId', lessonId),
					},
					null,
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUserGrammarLessonWordsReview(endUserId: number, lessonId: number) {
		const req =
			this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
				.GET_END_USER_GRAMMAR_LESSON_WORDS_REVIEW;
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					{
						...req,
						url: req.url
							.replace(':endUserId', endUserId)
							.replace(':lessonId', lessonId),
					},
					null,
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUserSortingLessonWordsReview(endUserId: number, lessonId: number) {
		const req =
			this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
				.GET_END_USER_SORTING_LESSON_WORDS_REVIEW;
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					{
						...req,
						url: req.url
							.replace(':endUserId', endUserId)
							.replace(':lessonId', lessonId),
					},
					null,
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getEndUserSightLessonWordsReview(endUserId: number, lessonId: number) {
		const req =
			this.applicationContext.getApiConfig().END_USERS_MANAGEMENT
				.GET_END_USER_SIGHT_WORDS_LESSON_WORDS_REVIEW;
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					{
						...req,
						url: req.url
							.replace(':endUserId', endUserId)
							.replace(':lessonId', lessonId),
					},
					null,
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	private formatEndUsersSearchQuery(searchQuery: EndUsersSearchQuery) {
		return deleteEmptyObjectProperties(
			searchQuery,
			[-1, '-1'],
			['orderByOrder'],
		);
	}
}
